import React, { useEffect, useRef, useState } from 'react';
import celebration from '../styles/TodoCompletionModal.module.css';

/**
 * PlanCelebration — the full-screen "you're a member now" moment, in the
 * app's celebration language (video + confetti + serif headline, same
 * stylesheet as TodoCompletionModal's "Mission Complete").
 *
 * Shared by every path that completes a purchase:
 *  - CheckoutReturn (back from real Stripe checkout)
 *  - MockCheckout success (demo/mock mode)
 *
 * Props:
 *  - hasTogether: boolean — Together copy vs base-plan copy
 *  - onDone: () => void — CTA action (reload / close, caller decides)
 */
const CELEBRATION_VIDEO = '/celebrations/Investing in Your Future.mp4';
const CONFETTI_COLORS = [
  '#4a9d7f', '#6e8e87', '#3a7d6f', '#5fb89c', '#2d6b5f',
  '#ff6b6b', '#ffd93d', '#6bcf7f', '#4ecdc4', '#95e1d3',
  '#a8dadc', '#457b9d', '#f4a261', '#e76f51', '#2a9d8f'
];

function PlanCelebration({ hasTogether = false, onDone }) {
  const [showContent, setShowContent] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => { /* autoplay blocked — gradient stays */ });
    }
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={celebration.fullscreenOverlay}>
      <video
        ref={videoRef}
        className={celebration.backgroundVideo}
        src={CELEBRATION_VIDEO}
        loop
        muted
        playsInline
        preload="auto"
      />
      <div className={celebration.overlay} />

      <div className={celebration.celebrationBackground}>
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className={celebration.confetti}
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
              backgroundColor: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)]
            }}
          />
        ))}
      </div>

      <div className={`${celebration.celebrationContent} ${showContent ? celebration.visible : ''}`}>
        <h1 className={celebration.celebrationTitle}>
          {hasTogether ? 'Welcome to Sophia Together!' : 'Welcome to Sophia!'}
        </h1>
        <p className={celebration.celebrationSubtitle}>
          {hasTogether
            ? 'Your plan is active — and you can now bring the people you love with you.'
            : 'Your subscription is active. Your money coach is ready when you are.'}
        </p>
        <p className={celebration.celebrationMessage}>
          {hasTogether
            ? 'Invite family members or friends from the Sophia Together screen — each gets their own private Sophia, covered by you.'
            : 'Let’s take the first step toward your financial goals — together.'}
        </p>
        <button className={celebration.primaryButton} onClick={onDone}>
          {hasTogether ? 'Continue to Sophia' : 'Start your journey'}
        </button>
      </div>
    </div>
  );
}

export default PlanCelebration;
